import Overlay from "ol/Overlay";
import { fromLonLat } from "ol/proj";
import mapboxgl from "mapbox-gl";
import { isCluster, getClusterExpansionZoom } from "./clusterUtils";
import { googleDirectionIcon } from "./googleDirectionIcon";
import sprite from "../../assets/icons.svg";

/**
 * Cluster rendering utilities for the 3 map providers.
 *
 * Each renderer:
 * 1. Clears previously rendered cluster markers
 * 2. Renders new cluster markers and individual point markers
 * 3. Handles click events (cluster click → zoom in, point click → marker click handler)
 */

// ─── Shared: Create cluster DOM element ───
function createClusterElement(count) {
  const el = document.createElement("div");
  el.className = "qi-map-cluster-marker";
  el.setAttribute("role", "button");
  el.setAttribute("tabindex", "0");
  el.setAttribute("aria-label", `Cluster of ${count} markers`);

  const countSpan = document.createElement("span");
  countSpan.className = "qi-map-cluster-marker_count";
  countSpan.textContent = String(count);
  el.appendChild(countSpan);

  // Size scaling based on count
  if (count >= 100) {
    el.classList.add("qi-map-cluster-marker--large");
  } else if (count >= 10) {
    el.classList.add("qi-map-cluster-marker--medium");
  }

  return el;
}

// ═══════════════════════════════════════════════════════════════════════════════
// QIMap (OpenLayers) Cluster Renderer
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Renders cluster markers as DOM overlays on QIMap (OpenLayers).
 *
 * @param {Object} mapRef - QIMap ref
 * @param {Array} clusters - Array of cluster/point features from supercluster
 * @param {Object} clusterIndex - Supercluster instance
 * @param {Function} onClusterClick - Handler for cluster click (zooms in)
 * @param {Function} onMarkerClick - Handler for individual marker click
 */
export function renderQiMapClusterMarkers(mapRef, clusters, clusterIndex, onClusterClick, onMarkerClick) {
  if (!mapRef || typeof mapRef.getMapReference !== "function") return;
  const olMap = mapRef.getMapReference();
  if (!olMap || typeof olMap.addOverlay !== "function") return;

  // Initialize cache
  if (!olMap.__clusterOverlays) {
    olMap.__clusterOverlays = new Map();
  }
  const overlays = olMap.__clusterOverlays;
  const seen = new Set();

  (clusters || []).forEach((feature) => {
    const featureIsCluster = isCluster(feature);
    const key = featureIsCluster
      ? `cluster_${feature.properties.cluster_id}`
      : `point_${feature.properties.plate_number || feature.properties.imei || feature.properties._index}`;

    seen.add(key);
    const [lng, lat] = feature.geometry.coordinates;

    if (featureIsCluster) {
      const count = feature.properties.point_count;
      let entry = overlays.get(key);

      if (!entry) {
        const el = createClusterElement(count);

        el.onclick = (e) => {
          e.stopPropagation();
          onClusterClick && onClusterClick(feature);
        };
        el.onkeydown = (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.stopPropagation();
            onClusterClick && onClusterClick(feature);
          }
        };

        const overlay = new Overlay({
          element: el,
          positioning: "center-center",
          stopEvent: true,
          offset: [0, 0],
          className: "ol-overlay-container ol-selectable qi-map-cluster-overlay",
        });
        olMap.addOverlay(overlay);
        entry = { overlay, el, type: "cluster", count };
        overlays.set(key, entry);
      } else if (entry.count !== count) {
        // Update count text
        const countSpan = entry.el.querySelector(".qi-map-cluster-marker_count");
        if (countSpan) countSpan.textContent = String(count);
        entry.count = count;

        // Update size class
        entry.el.classList.remove("qi-map-cluster-marker--large", "qi-map-cluster-marker--medium");
        if (count >= 100) {
          entry.el.classList.add("qi-map-cluster-marker--large");
        } else if (count >= 10) {
          entry.el.classList.add("qi-map-cluster-marker--medium");
        }
      }

      entry.overlay.setPosition(fromLonLat([lng, lat]));
    }
    // Individual points are handled by existing label renderers (renderQiMapLabels / renderQiMapDeviceLabels)
  });

  // Remove stale cluster overlays
  overlays.forEach((entry, key) => {
    if (!seen.has(key) && entry.type === "cluster") {
      olMap.removeOverlay(entry.overlay);
      overlays.delete(key);
    }
  });
}

/**
 * Clears all cluster overlays from QIMap.
 * @param {Object} mapRef - QIMap ref
 */
export function clearQiMapClusterMarkers(mapRef) {
  if (!mapRef || typeof mapRef.getMapReference !== "function") return;
  const olMap = mapRef.getMapReference();
  if (!olMap || !olMap.__clusterOverlays) return;
  olMap.__clusterOverlays.forEach((entry) => {
    olMap.removeOverlay(entry.overlay);
  });
  olMap.__clusterOverlays.clear();
}

// ═══════════════════════════════════════════════════════════════════════════════
// Mapbox Cluster Renderer
// ═══════════════════════════════════════════════════════════════════════════════

const MAPBOX_CLUSTER_SOURCE = "tracking-cluster-source";
const MAPBOX_CLUSTER_LAYER = "tracking-clusters";
const MAPBOX_CLUSTER_COUNT_LAYER = "tracking-cluster-count";
const MAPBOX_UNCLUSTERED_LAYER = "tracking-unclustered-point";

/**
 * Renders clustered markers on Mapbox using its native clustering via GeoJSON source.
 * This approach uses Mapbox's built-in cluster rendering for better performance.
 *
 * @param {Object} mapRef - Mapbox GL JS map instance
 * @param {Array} trackingPoints - Raw tracking points (not pre-clustered)
 * @param {Function} onMarkerClick - Handler for individual marker click
 * @param {string} selectedPlate - Currently selected plate_number (to highlight)
 * @param {Object} options - { radius, maxZoom }
 */
export function renderMapboxClusters(mapRef, trackingPoints, onMarkerClick, selectedPlate, options = {}) {
  if (!mapRef || typeof mapRef.addSource !== "function") return;

  const radius = options.radius || 60;
  const maxZoom = options.maxZoom || 16;

  // Clean up previous cluster layers
  cleanupMapboxClusters(mapRef);

  // Build GeoJSON feature collection
  const geojsonData = {
    type: "FeatureCollection",
    features: (trackingPoints || []).map((point) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [point.lng, point.lat],
      },
      properties: {
        plate_number: point.plate_number || "",
        direction: point.direction || 0,
        id: point.id || "",
        device_name_list: point.device_name_list || "",
        imei: point.imei || "",
      },
    })),
  };

  // Add clustered GeoJSON source
  mapRef.addSource(MAPBOX_CLUSTER_SOURCE, {
    type: "geojson",
    data: geojsonData,
    cluster: true,
    clusterMaxZoom: maxZoom,
    clusterRadius: radius,
  });

  // Cluster circles layer
  mapRef.addLayer({
    id: MAPBOX_CLUSTER_LAYER,
    type: "circle",
    source: MAPBOX_CLUSTER_SOURCE,
    filter: ["has", "point_count"],
    paint: {
      "circle-color": [
        "step",
        ["get", "point_count"],
        "#51bbd6", // < 10
        10,
        "#f1a03c", // 10-99
        100,
        "#f04438", // 100+
      ],
      "circle-radius": [
        "step",
        ["get", "point_count"],
        18, // < 10
        10,
        24, // 10-99
        100,
        30, // 100+
      ],
      "circle-stroke-width": 3,
      "circle-stroke-color": "#ffffff",
    },
  });

  // Cluster count label layer
  mapRef.addLayer({
    id: MAPBOX_CLUSTER_COUNT_LAYER,
    type: "symbol",
    source: MAPBOX_CLUSTER_SOURCE,
    filter: ["has", "point_count"],
    layout: {
      "text-field": ["get", "point_count_abbreviated"],
      "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
      "text-size": 13,
    },
    paint: {
      "text-color": "#ffffff",
    },
  });

  // Unclustered individual points layer
  mapRef.addLayer({
    id: MAPBOX_UNCLUSTERED_LAYER,
    type: "symbol",
    source: MAPBOX_CLUSTER_SOURCE,
    filter: ["!", ["has", "point_count"]],
    layout: {
      "icon-image": "custom-icon",
      "icon-size": 0.6,
      "icon-rotate": ["get", "direction"],
      "icon-rotation-alignment": "map",
      "icon-allow-overlap": true,
      "text-field": ["get", "plate_number"],
      "text-size": 12,
      "text-offset": [0, -1.5],
      "text-anchor": "top",
      "text-allow-overlap": true,
    },
    paint: {
      "text-color": "#000000",
      "text-halo-color": "#ffffff",
      "text-halo-width": 2,
      "text-halo-blur": 0.5,
      "icon-opacity": selectedPlate
        ? ["case", ["==", ["get", "plate_number"], selectedPlate], 1, 0.6]
        : 1,
    },
  });

  // Click handler for clusters → zoom in
  if (!mapRef.__clusterClickHandler) {
    mapRef.__clusterClickHandler = (e) => {
      const features = mapRef.queryRenderedFeatures(e.point, {
        layers: [MAPBOX_CLUSTER_LAYER],
      });
      if (!features.length) return;

      const clusterId = features[0].properties.cluster_id;
      mapRef.getSource(MAPBOX_CLUSTER_SOURCE).getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        mapRef.easeTo({
          center: features[0].geometry.coordinates,
          zoom: zoom,
          duration: 300,
        });
      });
    };
    mapRef.on("click", MAPBOX_CLUSTER_LAYER, mapRef.__clusterClickHandler);
  }

  // Click handler for unclustered points
  if (!mapRef.__unclusteredClickHandler) {
    mapRef.__unclusteredClickHandler = (e) => {
      const features = mapRef.queryRenderedFeatures(e.point, {
        layers: [MAPBOX_UNCLUSTERED_LAYER],
      });
      if (!features.length) return;
      const props = features[0].properties;
      onMarkerClick && onMarkerClick(props);
    };
    mapRef.on("click", MAPBOX_UNCLUSTERED_LAYER, mapRef.__unclusteredClickHandler);
  }

  // Cursor style
  mapRef.on("mouseenter", MAPBOX_CLUSTER_LAYER, () => {
    mapRef.getCanvas().style.cursor = "pointer";
  });
  mapRef.on("mouseleave", MAPBOX_CLUSTER_LAYER, () => {
    mapRef.getCanvas().style.cursor = "";
  });
  mapRef.on("mouseenter", MAPBOX_UNCLUSTERED_LAYER, () => {
    mapRef.getCanvas().style.cursor = "pointer";
  });
  mapRef.on("mouseleave", MAPBOX_UNCLUSTERED_LAYER, () => {
    mapRef.getCanvas().style.cursor = "";
  });
}

/**
 * Cleans up Mapbox cluster layers and source.
 * @param {Object} mapRef - Mapbox GL JS map instance
 */
export function cleanupMapboxClusters(mapRef) {
  if (!mapRef || typeof mapRef.getLayer !== "function") return;

  // Remove click handlers
  if (mapRef.__clusterClickHandler) {
    mapRef.off("click", MAPBOX_CLUSTER_LAYER, mapRef.__clusterClickHandler);
    mapRef.__clusterClickHandler = null;
  }
  if (mapRef.__unclusteredClickHandler) {
    mapRef.off("click", MAPBOX_UNCLUSTERED_LAYER, mapRef.__unclusteredClickHandler);
    mapRef.__unclusteredClickHandler = null;
  }

  // Remove layers
  [MAPBOX_CLUSTER_LAYER, MAPBOX_CLUSTER_COUNT_LAYER, MAPBOX_UNCLUSTERED_LAYER].forEach((layerId) => {
    if (mapRef.getLayer(layerId)) {
      mapRef.removeLayer(layerId);
    }
  });

  // Remove source
  if (mapRef.getSource(MAPBOX_CLUSTER_SOURCE)) {
    mapRef.removeSource(MAPBOX_CLUSTER_SOURCE);
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// Google Maps Cluster Renderer
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Renders clustered markers on Google Maps using supercluster data.
 *
 * @param {Object} mapRef - Google Maps instance
 * @param {Array} clusters - Array of cluster/point features from supercluster
 * @param {Object} clusterIndex - Supercluster instance for expansion zoom
 * @param {Function} onMarkerClick - Handler for individual marker click
 * @param {string} selectedPlate - Currently selected plate_number
 */
export function renderGoogleMapClusters(mapRef, clusters, clusterIndex, onMarkerClick, selectedPlate) {
  if (!mapRef || typeof mapRef.fitBounds !== "function") return;

  // Clear previous cluster markers
  clearGoogleMapClusters();

  // Initialize storage
  if (!window.googleClusterMarkers) {
    window.googleClusterMarkers = [];
  }
  if (!window.googleClusterOverlays) {
    window.googleClusterOverlays = [];
  }

  (clusters || []).forEach((feature) => {
    const [lng, lat] = feature.geometry.coordinates;
    const position = { lat, lng };

    if (isCluster(feature)) {
      const count = feature.properties.point_count;
      const clusterId = feature.properties.cluster_id;

      // Create cluster marker as a custom overlay
      const clusterDiv = createClusterElement(count);
      clusterDiv.addEventListener("click", (e) => {
        e.stopPropagation();
        const expansionZoom = getClusterExpansionZoom(clusterIndex, clusterId);
        mapRef.panTo(position);
        mapRef.setZoom(expansionZoom);
      });

      const overlay = new google.maps.OverlayView();
      overlay.onAdd = function () {
        const pane = overlay.getPanes().overlayMouseTarget;
        if (pane) {
          pane.appendChild(clusterDiv);
        }
      };
      overlay.draw = function () {
        const projection = overlay.getProjection();
        if (projection) {
          const point = projection.fromLatLngToDivPixel(new google.maps.LatLng(lat, lng));
          if (point) {
            clusterDiv.style.left = `${point.x}px`;
            clusterDiv.style.top = `${point.y}px`;
            clusterDiv.style.position = "absolute";
            clusterDiv.style.transform = "translate(-50%, -50%)";
          }
        }
      };
      overlay.onRemove = function () {
        if (clusterDiv.parentNode) {
          clusterDiv.parentNode.removeChild(clusterDiv);
        }
      };
      overlay.setMap(mapRef);
      window.googleClusterOverlays.push(overlay);
    } else {
      // Individual marker
      const point = feature.properties;

      const marker = new google.maps.Marker({
        position,
        map: mapRef,
        icon: googleDirectionIcon(point.direction),
        zIndex: point.plate_number === selectedPlate ? 10 : 1,
      });

      // Label overlay
      const labelDiv = document.createElement("div");
      labelDiv.className = "qi-vehicle-label";
      labelDiv.style.position = "absolute";
      labelDiv.style.transform = "translate(-50%, -100%)";
      labelDiv.style.marginTop = "0";
      labelDiv.style.cursor = "pointer";

      const statusIcon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      statusIcon.setAttribute("class", "qi-vehicle-label_icon");
      statusIcon.setAttribute("width", "18");
      statusIcon.setAttribute("height", "18");
      statusIcon.style.color = point.statusColor || "#4b5563";
      const use = document.createElementNS("http://www.w3.org/2000/svg", "use");
      use.setAttributeNS("http://www.w3.org/1999/xlink", "href", `${sprite}#${point.statusIcon}`);
      statusIcon.appendChild(use);
      labelDiv.appendChild(statusIcon);

      const textSpan = document.createElement("span");
      textSpan.className = "qi-vehicle-label_text";
      textSpan.textContent = point.plate_number || point.imei || "";
      labelDiv.appendChild(textSpan);

      labelDiv.addEventListener("click", (e) => {
        e.stopPropagation();
        onMarkerClick && onMarkerClick(point);
      });

      marker.addListener("click", () => {
        onMarkerClick && onMarkerClick(point);
      });

      const labelOverlay = new google.maps.OverlayView();
      labelOverlay.onAdd = function () {
        const pane = labelOverlay.getPanes().overlayMouseTarget;
        if (pane) {
          pane.appendChild(labelDiv);
        }
      };
      labelOverlay.draw = function () {
        const projection = labelOverlay.getProjection();
        if (projection) {
          const pt = projection.fromLatLngToDivPixel(new google.maps.LatLng(lat, lng));
          if (pt) {
            labelDiv.style.left = `${pt.x}px`;
            labelDiv.style.top = `${pt.y - 16}px`;
          }
        }
      };
      labelOverlay.onRemove = function () {
        if (labelDiv.parentNode) {
          labelDiv.parentNode.removeChild(labelDiv);
        }
      };
      labelOverlay.setMap(mapRef);

      window.googleClusterMarkers.push(marker);
      window.googleClusterOverlays.push(labelOverlay);
    }
  });
}

/**
 * Clears all Google Maps cluster markers and overlays.
 */
export function clearGoogleMapClusters() {
  if (window.googleClusterMarkers) {
    window.googleClusterMarkers.forEach((marker) => {
      try {
        if (marker && typeof marker.setMap === "function") {
          marker.setMap(null);
        }
      } catch (e) {}
    });
    window.googleClusterMarkers = [];
  }
  if (window.googleClusterOverlays) {
    window.googleClusterOverlays.forEach((overlay) => {
      try {
        if (overlay && typeof overlay.setMap === "function") {
          overlay.setMap(null);
        }
      } catch (e) {}
    });
    window.googleClusterOverlays = [];
  }
}

/**
 * Removes cluster artifacts owned by the active map provider.
 *
 * This is used when the tracking map switches from the multi-asset overview
 * to a selected asset. Disabling cluster calculation alone does not remove
 * markers that were already rendered by the provider.
 *
 * @param {Object} mapRef - Active map instance/ref
 * @param {string} mapProvider - qiMap, mapbox, or google
 */
export function clearMapClusters(mapRef, mapProvider) {
  if (!mapProvider || mapProvider === "qiMap") {
    clearQiMapClusterMarkers(mapRef);
  } else if (mapProvider === "mapbox") {
    cleanupMapboxClusters(mapRef);
  } else if (mapProvider === "google") {
    clearGoogleMapClusters();
  }
}
